// mouse.js
import { ref, computed, shallowRef } from 'vue'
import { Stack } from '@/utils/stackUtils'
import { TRANSITION_SIDE_DIRECTIONS } from '@/modules/core/constants/common'
import type { FormListInterface } from '@/modules/core/models/types'

export function useFormPagination() {
  const $form = ref<HTMLFormElement>()
  const $formChild = ref<HTMLFormElement>()
  const transitionSideDirection = ref(TRANSITION_SIDE_DIRECTIONS.RIGHT)

  const pages = shallowRef<FormListInterface>({})
  const currentPath = ref(new Stack<string>())
  const expectedPath = ref<string[]>([])
  const isFinished = ref(false)

  const hasPreviousPage = computed(() => {
    const previousPages = currentPath.value.size() - 1
    return previousPages > 0
  })
  const pageKey = computed(() => lastPageCurrentPath.value)
  const hasNextPage = computed(() => !!nextPages.value?.length)
  const lastPageCurrentPath = computed(() => currentPath.value.peek())
  const progress = computed(() => {
    const total = expectedPath.value.length
    const current = isFinished.value ? total : currentPath.value.size() - 1

    return {
      total,
      progress: current
    }
  })
  const currentPage = computed(() => {
    if (!lastPageCurrentPath.value) return null

    return pages.value[lastPageCurrentPath.value]
  })
  const currentPageComponent = computed(() => currentPage.value?.form)
  const currentPageExtra = computed(() => currentPage.value?.extra ?? {})
  const nextPages = computed(() => {
    if (!lastPageCurrentPath.value || !pages.value[lastPageCurrentPath.value]) {
      return []
    }

    return pages.value[lastPageCurrentPath.value].next
  })

  function initializePagination(
    newPages: FormListInterface,
    firstPage?: string,
    useSecondPath = false
  ) {
    if (!Object.keys(pages).length) return

    pages.value = newPages
    const currentPathFirstPage = Object.keys(pages.value)[0]
    const currentPathLastPage = firstPage ?? currentPathFirstPage

    const preloadPages = _pathFinder({
      pages: pages.value,
      currentPath: currentPath.value as Stack<string>,
      path: [currentPathFirstPage],
      lastPage: currentPathLastPage,
      useSecondPath
    })

    currentPath.value = new Stack<string>(preloadPages)
    expectedPath.value = getExpectedPath(pages.value, useSecondPath)
  }

  function getExpectedPath(pages: FormListInterface, useSecondPath: boolean = false) {
    if (!Object.keys(pages).length) return []

    return _pathFinder({
      pages: pages,
      currentPath: currentPath.value as Stack<string>,
      useSecondPath
    })
  }

  function _pathFinder({
    pages,
    currentPath,
    lastPage,
    path = [Object.keys(pages)[0]],
    useSecondPath = false
  }: {
    pages: FormListInterface
    currentPath: Stack<string>
    path?: string[]
    lastPage?: string
    useSecondPath?: boolean
  }): string[] {
    const currentPage = path[path.length - 1]
    const nextPage = pages[currentPage]?.next

    if (!nextPage) {
      return path
    }

    if (lastPage === currentPage || nextPage?.length === 0) {
      return path
    }

    if (path.includes(nextPage[0])) {
      return path
    }

    const nextPageIndex = useSecondPath ? nextPage.length - 1 : 0
    const page = nextPage[nextPageIndex]
    path.push(page)

    return _pathFinder({
      pages,
      currentPath,
      path,
      lastPage,
      useSecondPath
    })
  }

  function showExpectedPath() {
    return expectedPath.value
  }

  function goToPrevPage() {
    transitionSideDirection.value = TRANSITION_SIDE_DIRECTIONS.LEFT
    currentPath.value.pop()
  }

  async function goToNextPage(page: string) {
    transitionSideDirection.value = TRANSITION_SIDE_DIRECTIONS.RIGHT
    currentPath.value.push(page)
  }

  function redirectTo(page: string) {
    transitionSideDirection.value = TRANSITION_SIDE_DIRECTIONS.RIGHT
    const expectedPageIndex = expectedPath.value.findIndex((e) => e === page)
    const newExpectedPath = expectedPath.value.slice(0, expectedPageIndex + 1)

    currentPath.value = new Stack<string>(newExpectedPath)
  }

  function skipToNextPage(page: string) {
    transitionSideDirection.value = TRANSITION_SIDE_DIRECTIONS.RIGHT
    currentPath.value.push(page)
  }

  function finishProgressBar() {
    isFinished.value = true
  }

  async function validateForm() {
    if (!$form.value) throw new Error('$form is not defined ')

    const { valid } = await $form.value.validate()

    return valid
  }

  async function submitFormChild() {
    return await $formChild.value?.triggerSubmit()
  }

  function getPageRuntimeGuard(page: string) {
    return pages.value[page]?.extra?.runtimeGuard
  }

  return {
    $form,
    $formChild,

    transitionSideDirection,
    currentPath,
    pageKey,
    hasPreviousPage,
    hasNextPage,
    progress,
    currentPageComponent,
    currentPageExtra,
    nextPages,

    initializePagination,
    redirectTo,
    goToNextPage,
    goToPrevPage,
    skipToNextPage,
    validateForm,
    submitFormChild,
    finishProgressBar,
    showExpectedPath,
    getPageRuntimeGuard
  }
}
